import { MailOutlined } from '@ant-design/icons';
import { useLocation } from '@umijs/max';
import {
  Badge,
  Button,
  Checkbox,
  Modal,
  message,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createStyles } from 'antd-style';
import React, { useEffect, useState } from 'react';
import { getNotificationPreference } from '@/pages/account/service';
import {
  fetchErrorNoticeList,
  markErrorNoticeRead,
} from '@/services/errorNotice';
import { userCache } from '@/store/userInfo';

const { Paragraph, Text } = Typography;

const useStyles = createStyles(({ token }) => ({
  action: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '48px',
    minWidth: 48,
    padding: '0 12px',
    cursor: 'pointer',
    borderRadius: token.borderRadius,
    color: 'inherit',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: token.colorBgTextHover,
    },
  },
  icon: {
    fontSize: 18,
    color: 'inherit',
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  summary: {
    color: token.colorTextSecondary,
  },
}));

type ErrorNoticeItem = Awaited<
  ReturnType<typeof fetchErrorNoticeList>
>['list'][number];

const ErrorNotice: React.FC = () => {
  const { styles } = useStyles();
  const location = useLocation();
  const [, forceRender] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ErrorNoticeItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const isSuperAdmin = userCache.isSuperAdmin === 1;
  const isCataloguePage = location.pathname.startsWith('/catalogue');

  useEffect(() => {
    const handler = () => forceRender((v) => v + 1);
    window.addEventListener('user-cache-update', handler);
    return () => window.removeEventListener('user-cache-update', handler);
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    void (async () => {
      try {
        const res = await getNotificationPreference();
        const data = (res as any)?.data ?? res;
        const enabled = Number(data?.emailEnabled ?? 0) === 1 ? 1 : 0;
        if (userCache.emailEnabled !== enabled) {
          userCache.emailEnabled = enabled;
          window.dispatchEvent(new Event('user-cache-update'));
        }
      } catch (_error) {
        // ignore
      }
    })();
  }, [isSuperAdmin]);

  const loadNotice = async () => {
    if (!isSuperAdmin || userCache.emailEnabled !== 1) {
      setUnreadCount(0);
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchErrorNoticeList(20);
      const data = (res as any)?.data ?? res;
      setItems(Array.isArray(data?.list) ? data.list : []);
      setUnreadCount(Number(data?.unreadCount || 0));
    } catch (_error) {
      message.error('加载错误消息失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin || isCataloguePage || userCache.emailEnabled !== 1) {
      setUnreadCount(0);
      setItems([]);
      return undefined;
    }
    void loadNotice();
  }, [isSuperAdmin, isCataloguePage]);

  useEffect(() => {
    if (
      !isSuperAdmin ||
      isCataloguePage ||
      !open ||
      userCache.emailEnabled !== 1
    ) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void loadNotice();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [isSuperAdmin, isCataloguePage, open]);

  const allSelected =
    items.length > 0 && selectedRowKeys.length === items.length;
  const indeterminate =
    selectedRowKeys.length > 0 && selectedRowKeys.length < items.length;

  const columns: ColumnsType<ErrorNoticeItem> = [
    {
      title: '状态',
      dataIndex: 'isRead',
      width: 90,
      render: (_, record) =>
        record.isRead === 1 ? (
          <Tag color="green">已读</Tag>
        ) : (
          <Tag color="red">未读</Tag>
        ),
    },
    {
      title: '错误信息',
      dataIndex: 'errorMsg',
      width: 320,
      render: (value: string, record) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text strong={!record.isRead}>{value || '-'}</Text>
          <Text type="secondary">
            {record.exceptionType || '-'} · {record.method || '-'}{' '}
            {record.url || '-'}
          </Text>
        </Space>
      ),
    },
    {
      title: '应用',
      dataIndex: 'appName',
      width: 180,
      render: (value: string, record) => (
        <Space direction="vertical" size={0}>
          <Text>{value || '-'}</Text>
          <Text type="secondary">{record.appUuid || '-'}</Text>
        </Space>
      ),
    },
    {
      title: '时间',
      dataIndex: 'createdAt',
      width: 170,
    },
  ];

  const handleMarkRead = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要标记已读的消息');
      return;
    }
    const ids = selectedRowKeys
      .map((key) => Number(key))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (ids.length === 0) {
      message.warning('请选择有效的消息');
      return;
    }
    try {
      await markErrorNoticeRead(ids);
      message.success('已标记已读');
      setSelectedRowKeys([]);
      await loadNotice();
    } catch (_error) {
      message.error('标记已读失败');
    }
  };

  if (!isSuperAdmin || userCache.emailEnabled !== 1) {
    return null;
  }

  return (
    <>
      <div
        className={styles.action}
        onClick={() => {
          setOpen(true);
          void loadNotice();
        }}
        title="错误消息"
      >
        <Badge count={unreadCount} overflowCount={99} offset={[0, 0]}>
          <MailOutlined className={styles.icon} />
        </Badge>
      </div>
      <Modal
        open={open}
        title="错误消息"
        width={980}
        onCancel={() => {
          setOpen(false);
          setSelectedRowKeys([]);
        }}
        footer={null}
        destroyOnClose={false}
      >
        <div className={styles.modalBody}>
          <div className={styles.toolbar}>
            <Space>
              <Checkbox
                indeterminate={indeterminate}
                checked={allSelected}
                onChange={(event) => {
                  if (event.target.checked) {
                    setSelectedRowKeys(items.map((item) => item.id));
                  } else {
                    setSelectedRowKeys([]);
                  }
                }}
              >
                全选
              </Checkbox>
              <Text className={styles.summary}>
                共 {items.length} 条，未读 {unreadCount} 条
              </Text>
            </Space>
            <Space>
              <Button onClick={() => void loadNotice()} loading={loading}>
                刷新
              </Button>
              <Button
                type="primary"
                disabled={selectedRowKeys.length === 0}
                onClick={() => void handleMarkRead()}
              >
                标记已读
              </Button>
            </Space>
          </div>
          <Table<ErrorNoticeItem>
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={items}
            pagination={false}
            size="small"
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
            expandable={{
              rowExpandable: (record) =>
                Boolean(
                  record.params ||
                    record.stackTrace ||
                    record.clientIp ||
                    record.userAgent,
                ),
              expandedRowRender: (record) => (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Paragraph style={{ marginBottom: 0 }}>
                    <Text strong>参数：</Text>
                    <Text code>{record.params || '-'}</Text>
                  </Paragraph>
                  <Paragraph style={{ marginBottom: 0 }}>
                    <Text strong>堆栈：</Text>
                    <Text code style={{ whiteSpace: 'pre-wrap' }}>
                      {record.stackTrace || '-'}
                    </Text>
                  </Paragraph>
                  <Space wrap>
                    <Tag>状态码 {record.statusCode}</Tag>
                    <Tag>IP {record.clientIp || '-'}</Tag>
                    <Tag>阅读时间 {record.readAt || '-'}</Tag>
                  </Space>
                </Space>
              ),
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default ErrorNotice;
